/**
 * UI state store using Zustand.
 * Manages UI-specific state like selected nodes, filters, search queries, etc.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface UIState {
  // Selected entities
  selectedVlanId: number | null;
  selectedInterfaceId: string | null;
  selectedSwitchHostname: string | null;

  // Search and filters
  searchQuery: string;
  filters: {
    status?: 'active' | 'suspended' | 'up' | 'down';
    mode?: 'access' | 'trunk';
    vlanId?: number;
  };

  // UI controls
  sidebarOpen: boolean;
  darkMode: boolean;

  // Actions
  setSelectedVlan: (id: number | null) => void;
  setSelectedInterface: (id: string | null) => void;
  setSelectedSwitch: (hostname: string | null) => void;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: UIState['filters']) => void;
  toggleSidebar: () => void;
  toggleDarkMode: () => void;
  clearSelections: () => void;
}

export const useUIStore = create<UIState>()(
  immer((set) => ({
    // Initial state
    selectedVlanId: null as number | null,
    selectedInterfaceId: null as string | null,
    selectedSwitchHostname: null as string | null,
    searchQuery: '',
    filters: {} as UIState['filters'],
    sidebarOpen: true,
    darkMode: false,

    // Actions
    setSelectedVlan: (id) => {
      set((state) => {
        state.selectedVlanId = id;
      });
    },

    setSelectedInterface: (id) => {
      set((state) => {
        state.selectedInterfaceId = id;
      });
    },

    setSelectedSwitch: (hostname) => {
      set((state) => {
        state.selectedSwitchHostname = hostname;
      });
    },

    setSearchQuery: (query) => {
      set((state) => {
        state.searchQuery = query;
      });
    },

    setFilters: (filters) => {
      set((state) => {
        state.filters = filters;
      });
    },

    toggleSidebar: () => {
      set((state) => {
        state.sidebarOpen = !state.sidebarOpen;
      });
    },

    toggleDarkMode: () => {
      set((state) => {
        state.darkMode = !state.darkMode;
      });
    },

    clearSelections: () => {
      set((state) => {
        state.selectedVlanId = null;
        state.selectedInterfaceId = null;
        state.selectedSwitchHostname = null;
      });
    },
  }))
);
